import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { HeartHandshake, Check, Eye, EyeOff } from "lucide-react";
import api, { apiError } from "../lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const inputCls = "w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 bg-white px-4 text-lg focus:outline-none focus:border-primary";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match — please retype them.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-background flex flex-col">
      <header className="max-w-5xl w-full mx-auto px-4 py-4">
        <Link to="/" data-testid="home-link" className="flex items-center gap-2 w-fit">
          <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
            <HeartHandshake size={24} strokeWidth={2.5} className="text-white" />
          </span>
          <span className="text-xl font-extrabold tracking-tight">RoutineBuddy</span>
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 pt-8 pb-12">
        <div className="w-full max-w-md bg-white rounded-3xl border-2 border-foreground/10 p-8">
          {done ? (
            <div data-testid="reset-success-message">
              <Check size={40} strokeWidth={2.5} className="text-[#38A169] mb-4" />
              <h1 className="text-2xl font-extrabold">Password changed</h1>
              <p className="mt-2 text-lg text-muted-foreground">Taking you to log in…</p>
            </div>
          ) : !token ? (
            <div data-testid="reset-missing-token">
              <h1 className="text-2xl font-extrabold">Missing reset link</h1>
              <p className="mt-2 text-lg text-muted-foreground">Use the link from your email, or request a new one.</p>
              <Link to="/forgot-password" data-testid="request-new-link" className="mt-6 inline-block min-h-[56px] leading-[56px] px-6 rounded-full bg-primary text-white font-bold">Request a new link</Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl font-extrabold">Choose a new password</h1>
              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-base font-bold mb-1">New password</label>
                  <div className="relative">
                    <input id="new-password" type={showPw ? "text" : "password"} data-testid="new-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className={`${inputCls} pr-14`} placeholder="At least 8 characters" />
                    <button type="button" data-testid="toggle-new-password-visibility" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] grid place-items-center rounded-full hover:bg-secondary transition-colors duration-200">
                      {showPw ? <EyeOff size={22} strokeWidth={2.5} /> : <Eye size={22} strokeWidth={2.5} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="confirm-new-password" className="block text-base font-bold mb-1">Confirm new password</label>
                  <div className="relative">
                    <input id="confirm-new-password" type={showConfirm ? "text" : "password"} data-testid="confirm-new-password-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className={`${inputCls} pr-14`} placeholder="Type it again" />
                    <button type="button" data-testid="toggle-confirm-new-visibility" onClick={() => setShowConfirm((v) => !v)} aria-label={showConfirm ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[44px] min-h-[44px] grid place-items-center rounded-full hover:bg-secondary transition-colors duration-200">
                      {showConfirm ? <EyeOff size={22} strokeWidth={2.5} /> : <Eye size={22} strokeWidth={2.5} />}
                    </button>
                  </div>
                </div>
                {error && <p data-testid="reset-error" className="text-[#C53030] font-semibold bg-[#F56565]/10 border-2 border-[#F56565]/40 rounded-2xl px-4 py-3">{error}</p>}
                <button type="submit" data-testid="reset-submit-button" disabled={busy} className="w-full min-h-[56px] rounded-full bg-primary text-white text-lg font-bold transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-60">
                  {busy ? "Saving…" : "Save new password"}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
